import React, { useState, useEffect } from 'react';
import {
  TextField,
  Button,
  Box,
  Typography,
  InputAdornment,
} from '@material-ui/core';
import SearchIcon from '@material-ui/icons/Search';
import { CustomModal } from '../../..';
import './features.css';
import { OverlayLoading } from '../../OverlayLoading/OverlayLoading';
import FeaturesListSection from './FeaturesListSection';

interface Feature {
  id: number;
  name: string;
  config: any;
  isvisible: boolean;
}

interface FixProps {
  show: boolean;
  onClose?: { (): void };
  features: any[]; // Array of feature objects with id, name, and optional max_limit
  allFeatures: Feature[]; // All available features from the document
  planName: string; // Plan name from parent
  planId: number | null; // Plan ID from parent
  companyName: string; // Company name from parent
  handleFeatureSubmit: (params: any) => Promise<void>;
  isSubmitting: boolean; // Loading state from parent
}

const ActiveFeaturesModal: React.FC<FixProps> = ({
  show,
  onClose,
  features,
  allFeatures,
  planName,
  planId,
  companyName,
  handleFeatureSubmit,
  isSubmitting,
}) => {
  const [selectedFeatures, setSelectedFeatures] = useState<number[]>([]);
  // const [featureLimits, setFeatureLimits] = useState<{ [key: number]: number }>(
  //   {}
  // );
  const [searchTerm, setSearchTerm] = useState('');

  // Update the useEffect to transform the features array
  useEffect(() => {
    if (features && allFeatures) {
      // Transform features array if it's an array of strings
      let featuresList = features;
      if (features.length > 0 && typeof features[0] === 'string') {
        // Convert feature names to feature objects with IDs
        featuresList = features
          .map((featureName: string) => {
            const feature = allFeatures.find(
              (f) => f.name.toUpperCase() === featureName.toUpperCase()
            );
            return feature ? { id: feature.id, name: feature.name } : null;
          })
          .filter(
            (item): item is { id: number; name: string } => item !== null
          );
      }

      const selectedIds = featuresList
        .map((feature: any) => {
          const allFeature = allFeatures.find((f) => f.id === feature.id);
          return allFeature && allFeature.isvisible ? feature.id : null;
        })
        .filter((id): id is number => id !== null);

      // Remove duplicates using Set
      const uniqueSelectedIds = Array.from(new Set(selectedIds));

      setSelectedFeatures(uniqueSelectedIds);
      // setFeatureLimits({});
    }
  }, [features, allFeatures]);

  // Update handleFeatureToggle to remove limit handling
  const handleFeatureToggle = (featureId: number) => {
    if (isSubmitting) return;

    setSelectedFeatures((prev) => {
      if (prev.includes(featureId)) {
        return prev.filter((id) => id !== featureId);
      } else {
        return [...prev, featureId];
      }
    });
  };

  const handleLimitChange = () => {
    // setFeatureLimits((prev) => ({
    //   ...prev,
    //   [featureId]: limit,
    // }));
  };

  const handleSelectAll = () => {
    if (isSubmitting) return;

    const visibleFeatureIds = allFeatures
      .filter((feature) => feature.isvisible)
      .map((feature) => feature.id);
    setSelectedFeatures(visibleFeatureIds);
  };

  const handleDeselectAll = () => {
    if (isSubmitting) return;
    setSelectedFeatures([]);
    // setFeatureLimits({});
  };

  const handleSubmit = async () => {
    // Send array of feature IDs directly
    const payload = {
      planId: planId,
      PlanName: planName,
      PlanFeatures: selectedFeatures, // Just send the array of IDs
    };

    try {
      await handleFeatureSubmit(payload);
    } catch (error) {
      console.error('Error submitting features:', error);
    }
  };

  const filteredFeatures = allFeatures.filter(
    (feature) =>
      feature.isvisible &&
      feature.name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const selectedCount = selectedFeatures.length;
  const totalVisible = allFeatures.filter((f) => f.isvisible).length;

  // Fix the initialSelectedIds calculation with duplicate removal
  const initialSelectedIds = (() => {
    if (!features || features.length === 0) return [];

    // Transform features array if it's an array of strings
    let featuresList = features;
    if (typeof features[0] === 'string') {
      featuresList = features
        .map((featureName: string) => {
          const feature = allFeatures.find(
            (f) => f.name.toUpperCase() === featureName.toUpperCase()
          );
          return feature ? { id: feature.id, name: feature.name } : null;
        })
        .filter((item): item is { id: number; name: string } => item !== null);
    }

    const ids = featuresList
      .map((feature: any) => {
        const allFeature = allFeatures.find((f) => f.id === feature.id);
        return allFeature && allFeature.isvisible ? feature.id : null;
      })
      .filter((id): id is number => id !== null);

    // Remove duplicates using Set
    return Array.from(new Set(ids));
  })();

  const initialLimits: { [key: number]: number } = {};
  features.forEach((feature) => {
    if (feature.max_limit !== undefined) {
      initialLimits[feature.id] = feature.max_limit;
    }
  });

  const hasSelectionChanges =
    selectedFeatures.length !== initialSelectedIds.length ||
    !selectedFeatures.every((id) => initialSelectedIds.includes(id));

  // const hasLimitChanges =
  //   Object.keys(featureLimits).some(
  //     (key) => featureLimits[parseInt(key)] !== initialLimits[parseInt(key)]
  //   ) ||
  //   Object.keys(initialLimits).some(
  //     (key) => featureLimits[parseInt(key)] !== initialLimits[parseInt(key)]
  //   );

  const hasChanges = hasSelectionChanges;

  const isSaveDisabled =
    selectedFeatures.length === 0 || !hasChanges || isSubmitting;

  return (
    <CustomModal
      show={show}
      onClose={onClose}
      headerMsg={`Active Features - ${companyName} (${planName})`}
      isSubmitBtnDisabled={isSaveDisabled}
      submitFunc={handleSubmit}
      buttonMsg={
        isSubmitting ? 'Saving...' : `Save Features (${selectedCount})`
      }
    >
      <Box className="featuresContainer">
        {isSubmitting && <OverlayLoading />}

        <Box className="featuresHeaderSection">
          <Box className="featuresCountInfo">
            <Typography variant="body2" className="featuresCountText">
              {selectedCount} of {totalVisible} features selected
            </Typography>
            {hasChanges && (
              <Typography
                variant="caption"
                style={{
                  color: 'gray',
                  marginLeft: '8px',
                  fontSize: '10px',
                }}
              >
                (Unsaved changes)
              </Typography>
            )}
          </Box>

          <TextField
            fullWidth
            variant="outlined"
            size="small"
            placeholder="Search features..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="featuresSearchInput"
            disabled={isSubmitting}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon className="featuresSearchIcon" />
                </InputAdornment>
              ),
            }}
          />

          <Box className="featuresBulkActions">
            <Button
              variant="contained"
              className="featuresBulkBtn featuresSelectAllBtn"
              onClick={handleSelectAll}
              fullWidth
              disabled={isSubmitting}
            >
              Select All
            </Button>
            <Button
              variant="contained"
              className="featuresBulkBtn featuresDeselectBtn"
              onClick={handleDeselectAll}
              fullWidth
              disabled={isSubmitting}
            >
              Clear All
            </Button>
          </Box>
        </Box>

        {/* <FeaturesListSection
          filteredFeatures={filteredFeatures}
          selectedFeatures={selectedFeatures}
          featureLimits={featureLimits}
          activeFeatures={features}
          isSubmitting={isSubmitting}
          onFeatureToggle={handleFeatureToggle}
          onLimitChange={handleLimitChange}
        /> */}
        <FeaturesListSection
          filteredFeatures={filteredFeatures}
          selectedFeatures={selectedFeatures}
          featureLimits={{}} // Always empty for ActiveFeaturesModal
          activeFeatures={[]} // Empty array since we don't use it here
          isSubmitting={isSubmitting}
          onFeatureToggle={handleFeatureToggle}
          onLimitChange={handleLimitChange}
          featuresWithLimits={[]} // Empty array to hide all limits
        />
      </Box>
    </CustomModal>
  );
};

export default ActiveFeaturesModal;
